import { When } from "react-if";
import {
  currencyFormatter,
  IPaymentPlan,
  IPaymentPlanInstalment,
  toDecimal,
} from "shared";
import { Tooltip } from "@mui/material";

import { Button } from "../../Atoms/Button";
import { Paragraph } from "../../Atoms/Typography/Paragraph";
import { Notification } from "../../Atoms/Notification";
import { Divider } from "../../Atoms/Divider";
import { Badge } from "../../Atoms/Badge";
import { DatePopover } from "../DatePopover";

interface InvoicePaymentPlanDisplayProps {
  invoiceCurrency: string;
  plan?: IPaymentPlan;
  onRemovePlan?: () => void;
  onPayNextInstalment?: () => void;
  timezone: string;
}

export type InstalmentStatusColor = `green` | `red` | `yellow` | `blue`;

/** Paid, Overdue, Partially Paid or Scheduled, from the stored amounts and due date */
export function getInstalmentStatus(instalment: IPaymentPlanInstalment): { label: string; color: InstalmentStatusColor } {
  const dueDate = instalment.dueDate ? new Date(`${instalment.dueDate}T00:00:00`) : null;
  const amount = toDecimal(instalment.amountCents || 0);
  const amountPaid = toDecimal(instalment.amountCentsPaid || 0);
  const fullyPaid = amountPaid >= amount;
  const partiallyPaid = amountPaid > 0 && amountPaid < amount;
  const overdue = !!dueDate && !fullyPaid && dueDate.getTime() < new Date().setHours(0,0,0,0);

  return fullyPaid ? { label: `Paid`, color: `green` } :
    overdue ? { label: `Overdue`, color: `red` } :
      partiallyPaid ? { label: `Partially Paid`, color: `yellow` } :
        { label: `Scheduled`, color: `blue` };
}

export function InvoicePaymentPlanDisplay({
  invoiceCurrency,
  plan,
  onRemovePlan,
  onPayNextInstalment,
  timezone,
}: InvoicePaymentPlanDisplayProps) {
  if (!plan) {
    return null;
  }

  const fmtTop = currencyFormatter(invoiceCurrency);
  const totalPlannedCents = plan?.instalments?.reduce((sum: number, i) => sum + (i.amountCents || 0), 0) || 0;
  const totalPaidCents = plan?.instalments?.reduce((sum: number, i) => sum + (i.amountCentsPaid || 0), 0) || 0;
  const overallPct = Math.min(100, Math.max(0, Math.round((toDecimal(totalPaidCents) / (toDecimal(totalPlannedCents) || 1)) * 100)));

  return (
    <>
      <div className={ `flex items-baseline justify-between` }>
        <div>
          <h2 className={ `text-sm font-semibold leading-6 text-gray-900` }>{ `Payment Plan` }</h2>
          <Paragraph variant={ `secondary` }>
            { `This invoice has current payment plan in progress` }
          </Paragraph>
        </div>
        <div className={ `flex items-center gap-2` }>
          <When condition={ !!onRemovePlan }>
            <Button
              color={ `red` }
              size={ `s` }
              onClick={ () => onRemovePlan() }
            >
              { `Remove Payment Plan` }
            </Button>
          </When>
          <When condition={ !!onPayNextInstalment }>
            <Button
              color={ `green` }
              size={ `s` }
              onClick={ () => onPayNextInstalment() }
            >
              { `Pay Next Installment` }
            </Button>
          </When>
        </div>
      </div>

      <When condition={ !!plan?.warnings }>
        <Notification
          type={ `warning` }
          className={ `mt-3` }
        >
          { plan?.warnings }
        </Notification>
      </When>

      <div className={ `mt-3` }>
        <div className={ `h-1.5 w-full rounded-sm bg-gray-100` }>
          <div
            className={ `h-1.5 rounded-sm bg-paidniceGreen-400 transition-all` }
            style={ { width: `${overallPct}%` } }
          />
        </div>
        <Paragraph
          variant={ `help` }
          className={ `mt-1` }
        >
          { `Paid ` }{ fmtTop(toDecimal(totalPaidCents)) }{ ` of ` }{ fmtTop(toDecimal(totalPlannedCents)) }
        </Paragraph>
      </div>

      <Divider
        spacing={ `small` }
        weight={ `light` }
      />

      { (() => {
        const fmt = currencyFormatter(invoiceCurrency);

        return (
          <div
            key={ plan.id }
            className={ `mb-4` }
          >
            <ul
              role={ `list` }
              className={ `divide-y divide-gray-100 rounded-lg ring-1 ring-gray-100` }
            >
              {
                plan?.instalments?.map((instalment, idx: number) => {
                  const dueDate = instalment.dueDate ? new Date(`${instalment.dueDate}T00:00:00`) : null;
                  const amount = toDecimal(instalment.amountCents || 0);
                  const amountPaid = toDecimal(instalment.amountCentsPaid || 0);
                  const partiallyPaid = amountPaid > 0 && amountPaid < amount;
                  const status = getInstalmentStatus(instalment);

                  const progressPct = Math.min(100, Math.max(0, Math.round((amountPaid / (amount || 1)) * 100)));
                  const cumulativePlannedCents = plan?.instalments
                    ?.slice(0, idx + 1)
                    .reduce((sum: number, i) => sum + (i.amountCents || 0), 0) || 0;
                  const cumulativePlanned = toDecimal(cumulativePlannedCents);

                  return (
                    <li
                      key={ instalment.id }
                      className={ `p-3 sm:p-4` }
                    >
                      <div className={ `flex items-start justify-between gap-4` }>
                        <div className={ `min-w-0 flex-1` }>
                          <div className={ `flex items-center gap-2` }>
                            <Paragraph className={ `font-medium` }>
                              { `Installment ${idx + 1}${ plan?.instalments?.length ? ` of ${plan.instalments.length}` : `` }` }
                            </Paragraph>
                            <Badge
                              color={ status.color }
                              size={ `xs` }
                            >
                              { status.label }
                            </Badge>
                          </div>
                          {
                            dueDate ? (
                              <DatePopover
                                date={ instalment.dueDate as string }
                                labelFormat={ `dd LLL yyyy` }
                                organisationTimezone={ timezone }
                              />
                            ) : (
                              <Paragraph variant={ `secondary` }>
                                { `No due date` }
                              </Paragraph>
                            )
                          }

                          <When condition={ partiallyPaid }>
                            <div className={ `mt-2` }>
                              <div className={ `h-1 w-40 sm:w-56 rounded-sm bg-gray-100` }>
                                <div
                                  className={ `h-1 rounded-sm bg-paidniceGreen-400 transition-all` }
                                  style={ { width: `${progressPct}%` } }
                                />
                              </div>
                              <Paragraph
                                variant={ `help` }
                                className={ `mt-1` }
                              >
                                { `Paid ` }{ fmt(amountPaid) }{ ` of ` }{ fmt(amount) }
                              </Paragraph>
                            </div>
                          </When>
                        </div>
                        <div className={ `text-right` }>
                          <Paragraph className={ `text-base` }>
                            { fmt(amount) }
                          </Paragraph>
                          <Tooltip title={ `Total amount to be paid against invoice on or before this installment` }>
                            <span>
                              <Paragraph variant={ `help` }>
                                { `(` }{ fmt(cumulativePlanned) }{ `/` }{ fmt(toDecimal(totalPlannedCents)) }{ `)` }
                              </Paragraph>
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    </li>
                  );
                })
              }
            </ul>
          </div>
        );
      })() }
    </>
  );
}
